import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Users, ChevronDown, ChevronUp } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";

export default function KelompokPage() {
  const [list, setList] = useState([]);
  const [siswaList, setSiswaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: "", siswa_ids: [], pendidikan: "" });
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/kelompok"), api.get("/admin/siswa")])
      .then(([k, s]) => { setList(k.data); setSiswaList(s.data); })
      .catch((e) => toast.error(apiErrorMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startCreate = () => { setForm({ nama: "", siswa_ids: [], pendidikan: "" }); setEditing(null); setCreating(true); };
  const startEdit = (k) => { setForm({ nama: k.nama, siswa_ids: k.siswa_ids || [], pendidikan: k.pendidikan || "" }); setEditing(k); setCreating(true); };

  const toggleSiswa = (id) => setForm((f) => ({ ...f, siswa_ids: f.siswa_ids.includes(id) ? f.siswa_ids.filter((x) => x !== id) : [...f.siswa_ids, id] }));
  const selectAll = () => setForm((f) => ({ ...f, siswa_ids: siswaList.map((s) => s.id) }));
  const clearAll = () => setForm((f) => ({ ...f, siswa_ids: [] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nama.trim()) return toast.error("Nama kelompok wajib diisi");
    if (form.siswa_ids.length < 4) return toast.error("Pilih minimal 4 siswa");
    try {
      if (editing) {
        await api.put(`/admin/kelompok/${editing.id}`, form);
        toast.success("Kelompok diperbarui");
      } else {
        await api.post("/admin/kelompok", form);
        toast.success("Kelompok dibuat");
      }
      setCreating(false); setEditing(null);
      load();
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const remove = async (k) => {
    if (!window.confirm(`Hapus kelompok ${k.nama}?`)) return;
    try { await api.delete(`/admin/kelompok/${k.id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const siswaMap = Object.fromEntries(siswaList.map((s) => [s.id, s]));

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Master Data</div>
          <h1 className="font-heading text-3xl font-bold text-navy-900">Kelompok Siswa</h1>
          <p className="text-slate-500 mt-1 text-sm">Kelompokkan serdik untuk memudahkan pembuatan jadwal penilaian.</p>
        </div>
        <button onClick={startCreate} data-testid="kelompok-add-btn" className="bg-brass-500 hover:bg-brass-600 text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Buat Kelompok
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-white border border-brass-300 rounded-sm p-5 mb-6 animate-fade-in">
          <h3 className="font-heading font-bold text-navy-900 mb-4">{editing ? `Edit Kelompok: ${editing.nama}` : "Kelompok Baru"}</h3>
          <div className="mb-4">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Nama Kelompok</label>
            <input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Mis. Kelompok Garuda" data-testid="kelompok-input-nama" className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-brass-500 focus:border-brass-500 outline-none" />
          </div>
          <div className="mb-4">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Nama Pendidikan <span className="text-slate-400 normal-case">(tampil di dokumen Sosiometri/Observasi/Gabungan)</span></label>
            <input value={form.pendidikan} onChange={(e) => setForm({ ...form, pendidikan: e.target.value })} placeholder="Mis. Dikcabpa Penerbangan Abit Diktukpa TNI AD TA 2023" data-testid="kelompok-input-pendidikan" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-brass-500 focus:border-brass-500 outline-none" />
          </div>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase font-semibold text-slate-500">Anggota (min 4)</label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={selectAll} className="text-brass-600 hover:underline">Pilih semua</button>
                <span className="text-slate-300">|</span>
                <button type="button" onClick={clearAll} className="text-slate-500 hover:underline">Kosongkan</button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-sm p-2 grid grid-cols-2 md:grid-cols-3 gap-1 bg-slate-50">
              {siswaList.map((s) => {
                const checked = form.siswa_ids.includes(s.id);
                return (
                  <label key={s.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer text-sm ${checked ? "bg-brass-100 border border-brass-300" : "bg-white border border-slate-200 hover:border-slate-300"}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleSiswa(s.id)} className="accent-brass-500" />
                    <span className="truncate">{s.nama}</span>
                  </label>
                );
              })}
            </div>
            <div className="text-xs text-slate-500 mt-1">Terpilih: <span className="font-bold font-mono text-navy-900">{form.siswa_ids.length}</span></div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="px-4 py-2 border border-slate-300 text-sm rounded-sm hover:bg-slate-50">Batal</button>
            <button type="submit" data-testid="kelompok-save-btn" className="bg-navy-900 hover:bg-navy-800 text-white px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Simpan</button>
          </div>
        </form>
      )}

      <div className="space-y-2" data-testid="kelompok-list">
        {loading ? <div className="text-slate-500">Memuat...</div> : list.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500">Belum ada kelompok. Klik &quot;Buat Kelompok&quot;.</div>
          </div>
        ) : list.map((k) => {
          const isExp = expanded[k.id];
          return (
            <div key={k.id} className="bg-white border border-slate-200 rounded-sm">
              <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-sm bg-brass-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-brass-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-navy-900 truncate">{k.nama}</div>
                    <div className="text-xs text-slate-500">{k.siswa_count} serdik {k.pendidikan ? <span className="ml-2 text-slate-400">• {k.pendidikan}</span> : null}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setExpanded({ ...expanded, [k.id]: !isExp })} className="px-3 py-1.5 text-sm border border-slate-300 hover:bg-slate-50 rounded-sm flex items-center gap-1">{isExp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} Anggota</button>
                  <button onClick={() => startEdit(k)} className="p-1.5 text-blue-700 hover:bg-blue-50 rounded-sm" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(k)} className="p-1.5 text-red-700 hover:bg-red-50 rounded-sm" title="Hapus"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {isExp && (
                <div className="border-t border-slate-100 p-4 bg-slate-50 grid grid-cols-2 md:grid-cols-3 gap-1">
                  {k.siswa_ids.map((id) => siswaMap[id] ? (
                    <div key={id} className="text-sm text-slate-700 px-2 py-1 bg-white rounded-sm border border-slate-200">{siswaMap[id].nama} <span className="text-slate-400 font-mono text-xs">({siswaMap[id].nosis})</span></div>
                  ) : <div key={id} className="text-xs text-slate-400">[siswa dihapus]</div>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
